import Loader from '@components/framework/loader/Loader';
import {FormField} from '@components/system/FormField/FormField';
import {
  IProcedure,
  IProcedureInputField
} from '@shared/services/service-mappers/SmartCoderServiceMapper';
import {
  getAllProcedures,
  getProcedureInputFields
} from '@shared/services/SmartCoderServices';
import React, {useEffect, useState} from 'react';
import {Controller, UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import ProcedureInputFields from '../../ProcedureInputFields/ProcedureInputFields';
import {
  GENERATE_CPT_CODE_TAB_ID,
  SMART_CODER_PROCEDURE_TYPES
} from '@shared/constants/Mapping';

interface IGenerateSedationCPTCodeTab {
  clinicGroupId: number;
  clinicId: number;
  relatedId: number;
  relatedType: string;
  useFormMethods: UseFormMethods;
  updateSubmitButtonState: (arg: {
    isDisabled: boolean;
    isLoading: boolean;
  }) => void;
  defaultValues?: Record<string, any>;
  clearGeneratedCPTCode?: () => void;
  onTabChange?: (v: string) => void;
}
const GenerateSedationCPTCodeTab = (
  props: IGenerateSedationCPTCodeTab
): JSX.Element => {
  const {
    clinicGroupId,
    clinicId,
    relatedId,
    relatedType,
    useFormMethods,
    updateSubmitButtonState,
    defaultValues,
    clearGeneratedCPTCode,
    onTabChange
  } = props;

  const {t} = useTranslation();
  const {procedure_code_id: procedureId} = useFormMethods.watch([
    'procedure_code_id'
  ]);
  const {sedation} = useFormMethods.watch(['sedation']);
  let sedationProcedureId = null;
  if (sedation) {
    sedationProcedureId = sedation.procedure_code_id;
  }

  const [procedures, setProcedures] = useState({
    isLoading: false,
    data: [] as IProcedure[]
  });

  const [procedureFields, setProcedureFields] = useState({
    isLoading: false,
    data: [] as IProcedureInputField[]
  });

  useEffect(() => {
    const curFormValues = useFormMethods.getValues();
    const sedationKeys = Object.keys(curFormValues?.['sedation'] ?? {});
    for (const formKey of sedationKeys) {
      if (!['procedure_code_id'].includes(formKey)) {
        useFormMethods.setValue(`sedation.${formKey}`, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sedationProcedureId]);

  useEffect(() => {
    if (procedures.isLoading || procedureFields.isLoading) {
      updateSubmitButtonState({
        isDisabled: true,
        isLoading: true
      });
    } else {
      updateSubmitButtonState({
        isDisabled: false,
        isLoading: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedures.isLoading, procedureFields.isLoading]);

  useEffect(() => {
    const fetchProcedures = async () => {
      setProcedures(p => ({
        ...p,
        isLoading: true
      }));
      try {
        const procedureRes = await getAllProcedures({
          clinicGroupId,
          clinicId,
          params: {
            type: SMART_CODER_PROCEDURE_TYPES.SEDATION
          }
        });
        if (Array.isArray(procedureRes.data)) {
          setProcedures({
            data: procedureRes.data,
            isLoading: false
          });
        } else {
          setProcedures({
            data: [],
            isLoading: false
          });
        }
      } catch (err) {
        setProcedures({
          data: [],
          isLoading: false
        });
      }
    };
    fetchProcedures();
  }, [clinicGroupId, clinicId, relatedId, relatedType]);

  useEffect(() => {
    const fetchProcedureFields = async () => {
      try {
        setProcedureFields(p => ({
          ...p,
          isLoading: true
        }));

        const fieldsRes = await getProcedureInputFields({
          clinicGroupId: clinicGroupId,
          clinicId: clinicId,
          procedureId: sedationProcedureId
        });
        if (Array.isArray(fieldsRes.data)) {
          setProcedureFields({
            data: fieldsRes.data,
            isLoading: false
          });
        } else {
          setProcedureFields({
            data: [],
            isLoading: false
          });
        }
      } catch (err) {
        setProcedureFields({
          data: [],
          isLoading: false
        });
      }
    };
    if (sedationProcedureId) {
      fetchProcedureFields();
    } else {
      setProcedureFields({
        data: [],
        isLoading: false
      });
    }
  }, [sedationProcedureId, clinicGroupId, clinicId]);

  const shouldShowLoader = procedures.isLoading || procedureFields.isLoading;

  const handleChangeTabToProcedure = () => {
    if (onTabChange) {
      onTabChange(GENERATE_CPT_CODE_TAB_ID.PROCEDURE);
    }
  };

  const selectedSedationProcedure = procedures.data.find(
    p => p.id == sedationProcedureId
  );

  return (
    <div className="procedure-fields-wrapper">
      {!procedureId && (
        <div>
          <p>
            {t(
              'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SELECT_PROCEDURE_HELPER_TEXT',
              {
                code_label: 'Sedation/Anesthesia'
              }
            )}
            <span className="link-style" onClick={handleChangeTabToProcedure}>
              {t(
                'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.GO_TO_PROCEDURE_TAB_LINK_TEXT'
              )}
            </span>
          </p>
        </div>
      )}
      {!!procedureId && (
        <>
          <div className="procedure-left-fields-wrapper">
            <Controller
              control={useFormMethods.control}
              name={'sedation.procedure_code_id'}
              defaultValue={
                defaultValues?.sedation?.['procedure_code_id']
                  ? String(defaultValues?.sedation?.['procedure_code_id'])
                  : null
              }
              render={inputProps => {
                return (
                  <FormField
                    label={t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SEDATION_PROCEDURE_LABEL'
                    )}
                    type={'radio'}
                    placeholder={t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PROCEDURE_FILED_PLACEHOLDER'
                    )}
                    options={procedures.data.map(p => ({
                      label: p.label,
                      value: String(p.id)
                    }))}
                    required={false}
                    {...inputProps}
                    error={useFormMethods.errors['sedation.procedure_code_id']}
                  />
                );
              }}
            />
          </div>
          <div className="procedure-middle-fields-wrapper">
            {shouldShowLoader && <Loader />}
            {!shouldShowLoader && (
              <ProcedureInputFields
                key={sedationProcedureId}
                fields={procedureFields.data}
                defaultValues={defaultValues}
                procedureId={sedationProcedureId}
                useFormMethods={useFormMethods}
                onInputValueChange={() => {
                  clearGeneratedCPTCode();
                }}
                clearFieldValue={fieldName => {
                  useFormMethods.setValue(fieldName, null);
                  clearGeneratedCPTCode();
                }}
                fieldNamePrefix={'sedation'}
                procedureName={selectedSedationProcedure?.label}
              />
            )}
            {!sedationProcedureId && !shouldShowLoader && (
              <div>
                {t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.SELECT_PROCEDURE_LABEL'
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GenerateSedationCPTCodeTab;
